import type { FormFieldOptions } from "@tellescope/types-models";

export interface DateOffsetBounds {
  withinBounds: boolean;
  minDate?: Date;
  maxDate?: Date;
}

/**
 * Resolves an offset in `minDateOffsetMs`/`maxDateOffsetMs` into a concrete date. The offset is
 * anchored to local midnight today rather than the current instant, so a whole-day offset lands on
 * a day boundary and an offset of 0 means "today", not "the rest of today".
 */
export const dateFromOffsetMs = (offsetMs: number): Date => {
  const anchor = new Date();
  anchor.setHours(0, 0, 0, 0);
  return new Date(anchor.getTime() + offsetMs);
};

/**
 * Resolves a field's `minDateOffsetMs`/`maxDateOffsetMs` into concrete bounds and reports whether
 * `date` falls inside them. The resolved bounds are returned so callers can format them into error
 * messages.
 *
 * @param maxBoundary - `"midnight"` compares against the start of the maximum day; `"endOfDay"`
 *   pushes it to 23:59:59.999 so any time-of-day on that day is in range. The two date types want
 *   different answers here, as they did in legacy: a `date` carries a time-of-day that must not be
 *   rejected for being later than midnight on the maximum day (`hooks.tsx:1149-1165`), while a
 *   `dateString` is already midnight-normalized before it arrives (`hooks.tsx:1129-1147`).
 */
export const isDateWithinOffsetBounds = (
  date: Date,
  options: FormFieldOptions | undefined,
  { maxBoundary }: { maxBoundary: "midnight" | "endOfDay" }
): DateOffsetBounds => {
  const result: DateOffsetBounds = { withinBounds: true };

  if (options?.minDateOffsetMs !== undefined) {
    const minDate = dateFromOffsetMs(options.minDateOffsetMs);
    result.minDate = minDate;
    if (date < minDate) result.withinBounds = false;
  }

  if (options?.maxDateOffsetMs !== undefined) {
    const maxDate = dateFromOffsetMs(options.maxDateOffsetMs);
    if (maxBoundary === "endOfDay") maxDate.setHours(23, 59, 59, 999);
    result.maxDate = maxDate;
    if (date > maxDate) result.withinBounds = false;
  }

  return result;
};
